/**
 * Every string a student ever sees, in one file.
 *
 * Serbian, Latin script, second person singular. The English editing jargon the courses already
 * use stays English: export, workflow, hook, timeline, keyframe, short form, sound design,
 * color korekcija. Course and lesson titles are never translated — they have to match Skool
 * exactly or the student cannot find them.
 *
 * Keeping them here rather than inline is not tidiness. It is so the tone can be read end to end
 * in one sitting, which is the only way to catch the drift into cheerleading.
 */

import type { Osa } from './dvoboj';
import { counted, plural, VERBS, WORDS } from './lang';
import { Terrain } from './terrain';

export const copy = {
  brand: {
    name: 'TALAS',
    /* Shown once, on the login screen, under the wordmark. */
    tagline: 'Put kroz Talas Akademiju',
  },

  login: {
    title: 'Uđi',
    lead: 'Ukucaj mejl sa kojim si u Talas Akademiji.',
    emailLabel: 'Mejl',
    emailPlaceholder: 'ti@mejl.com',
    submit: 'POŠALJI KOD',
    sending: 'ŠALJEM…',
    sent: (email: string) => `Kod je otišao na ${email}.`,
    sentHint: 'Prepiši ga ovde. Radi i kad ti je mejl na telefonu, a igra na kompjuteru.',
    /* The code is the way in that survives mail and browser being on different devices. */
    codeLabel: 'Kod iz mejla',
    codePlaceholder: '000000',
    codeSubmit: 'UĐI',
    codeChecking: 'GLEDAM…',
    codeLinkHint: 'U mejlu je i link, ako si na istom uređaju.',
    codeBad: 'Pogrešan kod. Proveri pa probaj opet.',
    codeExpired: 'Kod je istekao. Pošalji novi.',
    codeLocked: 'Previše pogrešnih pokušaja. Pošalji novi kod.',
    again: 'Pošalji ponovo',
    notMember: 'Taj mejl nije u Talas Akademiji.',
    notMemberHint: 'Ako si član a ne prolazi, javi Marku. Verovatno si upisan drugim mejlom.',
    joinLink: 'Pogledaj Talas Akademiju',
    tooMany: 'Previše pokušaja. Sačekaj minut.',
    badEmail: 'To ne liči na mejl.',
    /* The magic link lands here and waits for a click, so mail scanners cannot burn it. */
    confirmTitle: 'Skoro',
    confirmLead: 'Klikni da uđeš.',
    confirmButton: 'UĐI',
    expired: 'Link je istekao.',
    expiredHint: 'Pošalji novi, važi 15 minuta.',
    used: 'Taj link si već iskoristio.',
    invalid: 'Link ne radi.',
    readOnly: 'Članstvo ti je isteklo. Napredak je tu, čeka te.',
  },

  onboarding: {
    /* The GDD's own line, and it is the right one: the question is identity, not activity. */
    question: 'Šta hoćeš da postaneš?',
    hint: 'Možeš da promeniš kasnije. Ništa se ne briše.',
    lookQuestion: 'Kako izgledaš',
    start: 'KRENI',
    soon: 'Još nije spremno',
  },

  nav: {
    sada: 'Sada',
    mapa: 'Mapa',
    dnevnik: 'Dnevnik',
    lik: 'Lik',
    /* „Moje ostrvo" je ime stvari; u navigaciji stoji jedna reč, kao i sve ostale. */
    dom: 'Moje',
    posada: 'Posada',
    napomene: 'Napomene',
  },

  sada: {
    header: 'SADA',
    timeLabel: 'Koliko vremena imaš',
    anyTime: 'bilo šta',
    details: 'DETALJI',
    open: 'OTVORI U SKOOL-U',

    lessonsLead: 'Pređi lekcije redom. Radi mini vežbe u hodu.',
    exerciseLead: 'Odradi vežbu, exportuj, okači u zajednicu.',
    bossLead: 'Ovo je veliki zadatak. Odvoji mu vreme, pa okači u zajednicu.',
    treasureLead: 'Nije zadatak. Tu je kad ti zatreba.',

    doneButton: 'ZAVRŠENO',
    postedButton: 'OKAČIO SAM',
    reviseButton: 'POPRAVIO SAM',

    revisionTag: 'POPRAVKA POSLE FEEDBACKA',
    revisionLead: 'Vrati se u projekat i primeni feedback.',
    revisionWhy: 'Ovaj korak vredi više od novog rada.',
    revisionDeferred: (time: string) => `Čeka te popravka. Treba ti oko ${time}.`,

    opensNext: (island: string) => `→ ovo te vodi na ostrvo ${island}`,
    clearsIsland: (island: string) => `→ ovim prelaziš ostrvo ${island}`,

    nothingFitsTitle: 'Ništa ne staje u toliko vremena',
    nothingFitsLead: (minutes: number, shortest: string) =>
      `Imaš ${minutes} min, a najkraće što je otvoreno traje ${shortest}.`,
    nothingFitsAlt: 'Pogledaj tuđi rad i ostavi komentar. I to se računa.',
    showAll: 'prikaži sve',

    lockedTitle: 'Stigao si do brave',
    /**
     * The lock used to be a card with nothing on it. Now it names one exercise, and these two lines
     * are the "one key" rule at work: the first is used when that exercise closes the whole debt by
     * itself, the second when it does not. Never both.
     */
    offerOpens: (island: string) => `→ ovim se otvara ostrvo ${island}`,
    offerLock: (blocker: string, island: string) => `${blocker} · ostrvo ${island}`,
    offerAnother: 'DRUGA VEŽBA',
    waitingTitle: 'Čekaš feedback',
    waitingLead: (title: string) =>
      `„${title}“ je okačen i čeka da ga neko pogleda. Zamoli za feedback ili javi ovde kad stigne.`,
    endTitle: 'Prešao si sve što je otvoreno',
    endLead: 'Nova ostrva stižu kad Marko doda nove kurseve.',

    waiting: (n: number) =>
      n === 1 ? 'Čekaš feedback na 1 rad' : `Čekaš feedback na ${counted(n, WORDS.rad)}`,
    waitingNudge: 'Stoji već neko vreme. Zamoli nekoga da pogleda.',
    askForFeedback: 'ZAMOLI ZA FEEDBACK',
    askDraft:
      'Okačio sam rad. Ako neko ima minut da pogleda i kaže šta bi menjao, hvala.',
    copied: 'Kopirano',
  },

  node: {
    steps: {
      do: 'Odradi i exportuj',
      post: 'Okači u zajednicu',
      postHint: 'Nalepi link na svoj post.',
      feedback: 'Sačekaj feedback',
      feedbackHint: 'Stigao je feedback.',
      revise: 'Primeni feedback i okači ponovo',
      reviseHint: 'Prva verzija pokazuje gde si. Druga pokazuje da si naučio.',
    },
    linkPlaceholder: 'link na tvoj post u zajednici',
    revisionLinkPlaceholder: 'link na popravljenu verziju',
    gotFeedback: 'DOBIO SAM FEEDBACK',
    gotFeedbackHint: 'Kad ti neko odgovori, javi ovde. Onda ide popravka.',
    lessonsIn: 'U Skool-u',
    lessonCount: (n: number) => `${counted(n, WORDS.lekcija)}`,
    locked: 'Zaključano',
    lockedHint: 'Ovo se otvara kad završiš prethodno.',
    optional: 'opciono',
    kinds: {
      lekcije: 'lekcije',
      vezba: 'vežba',
      veliki: 'veliki zadatak',
      riznica: 'riznica',
      opciono: 'opciono',
    },
    statuses: {
      zakljucan: 'zaključano',
      otvoren: '',
      zavrsen: 'završeno',
      okacen: 'čeka feedback',
      feedback: 'spremno za popravku',
      popravljen: 'popravljeno',
    },
  },

  island: {
    back: '← MAPA',
    of: (done: number, total: number) => `${done} od ${total}`,
    cleared: 'pređeno',
    /**
     * What stands where the fraction stands on every other island. A free island has no
     * denominator on purpose — „12 od 40" would be a third number, and one that says what you have
     * not done rather than what you have.
     */
    free: 'biraš šta praviš',
    freeMore: 'SVE VEŽBE',
    freeFewer: 'MANJE',
    freeMoreHint: 'Ako ti se ne radi nijedna od ovih.',
    goal: 'Cilj',
    alreadyKnow: 'VEĆ ZNAM OVO',
    alreadyKnowLead:
      'Okači jedan rad koji pokazuje da ovo već umeš. Ako mentor odobri, preskačeš ostrvo.',
    alreadyKnowSoon: 'Ovo još ne radi. Javi Marku, preskočiće te ručno.',
  },

  map: {
    title: 'Mapa',
    hint: 'Kreći se strelicama ili prevuci mapu. Enter da uđeš.',
    hintTouch: 'Prevuci mapu. Tapni ostrvo.',
    openSea: 'otvoreno more',
    zoomOut: 'CEO ARHIPELAG',
    zoomIn: 'NAZAD NA OSTRVO',
    horizonHint: 'Dalje se vidi kad stigneš.',
    you: 'ti',
  },

  /**
   * Synchronous play, which is deliberately almost wordless.
   *
   * There is no name for this feature in the product, no „uživo", no count of who is here and no
   * line for an empty island. Somebody who is on your island walks, and somebody who is not stands
   * where their next step is, exactly as they always have — and nothing anywhere explains the
   * difference, because there is nothing to explain and no version of that sentence that does not
   * end up announcing that you are alone.
   *
   * So this block is four things one member can say and two lines on a card.
   */
  uzivo: {
    /**
     * The whole vocabulary. Four, and it stays four.
     *
     * Each one has to survive being sent forty times: a question or an offer does, praise turns
     * into mockery, and a judgement between two members is what GDD §10.1 says poisons a community
     * that lives on feedback. All four are gender-free — half the crew are the female bodies.
     *
     * The last two are the point. „Ne ide mi." and „Pogledaću ti rad." are the ask and the answer:
     * the loop this whole product exists for, in two button presses.
     */
    gestovi: {
      cao: 'Ćao.',
      hvala: 'Hvala.',
      'ne-ide': 'Ne ide mi.',
      pogledacu: 'Pogledaću ti rad.',
    } as Record<string, string>,
    /** Desktop only, and only in the line that already tells you how to walk. */
    hint: '1–4 za poruku.',
    hintTouch: 'Drži A za poruku.',
    /**
     * On somebody's card, when the two of you are standing at the same task.
     *
     * No pronoun, and that is not a style choice: half the crew are the female bodies, and this
     * product never conjugates for another member — the same rule that keeps `posada.gotTitle` as
     * „Stigao ti je komentar", where „stigao" agrees with „komentar" and not with a person.
     */
    sameNode: 'Radi na istom čvoru kao ti.',
    /** On the card of somebody whose comment is still waiting for your revision. */
    owedRevision: 'Odavde ti je stigao komentar. Popravka te čeka.',
    openRevision: 'OTVORI POPRAVKU',
    /** The one protection: stop seeing somebody, on this device, without telling them. */
    hide: 'SAKRIJ',
    hidden: 'Sakriveno',
    hiddenBody: 'Više se ne pojavljuje na tvojoj mapi. Niko nije obavešten.',
    unhide: 'PRIKAŽI',
  },

  /**
   * The duel. Two people, one brief, one deadline, and a blind vote that says what decided it.
   *
   * The same rule as `posada` runs through every line: **never conjugate for another member.** The
   * subject is the thing, never the person — „Stigao je rad" because *rad* is masculine, with the
   * name beside it. Verbs about somebody else stay in the present tense, which carries no gender:
   * „Ana koristi RITAM" is safe where „Ana je predala" is a guess.
   *
   * The battle text is allowed one exclamation mark. It is the only place in this product that is,
   * because it is the only place pretending to be a Game Boy — and the rest of the file's rule
   * against strings of them still holds.
   */
  dvoboj: {
    title: 'Dvoboj',
    /* Never „izgubio si" anywhere. The verdict is a moment; the revision is the ending. */
    lead: 'Isti zadatak, isti rok. Zajednica bira — a ne zna koji je čiji.',

    /* offering one */
    challenge: 'IZAZOVI NA DVOBOJ',
    challengeWith: (name: string) => `Dvoboj — ${name}`,
    briefLabel: 'Zadatak',
    briefHint: 'Sve što ste oboje otključali. Može i nešto što si već radio.',
    /* A brief one of them has already posted. Offered, and said out loud rather than hidden. */
    redoTag: 'ponavljanje',
    redoMine: 'Ovo si već radio. Treba nov rad i nov link — ne broji se drugi put.',
    /* The subject is the work, never the person: „okačen" agrees with „zadatak", not with them. */
    redoTheirs: (name: string) => `Ovaj zadatak je već okačen — ${name}.`,
    noBriefs: 'Nemate nijedan zajednički zadatak. Niste na istom mestu na mapi.',
    workLabel: 'Rok za predaju',
    voteLabel: 'Rok za glasanje',
    /* Days rather than hours: „48 sati" and „2 dana" are the same thing, and one of them is read. */
    rok: (hours: number) => counted(Math.round(hours / 24), WORDS.dan),
    send: 'POŠALJI IZAZOV',
    sent: 'Izazov je poslat.',
    sentBody: 'Rok počinje tek kad druga strana prihvati.',

    /* answering one */
    incomingFrom: (name: string) => `${name} te zove na dvoboj.`,
    terms: (brief: string, work: string, vote: string) =>
      `${brief} · predaja za ${work} · glasanje traje ${vote}`,
    accept: 'PRIHVATAM',
    counter: 'PREDLOŽI DRUGO',
    /* Never „NE". The softest refusal that is still a refusal, and it needs no reason. */
    decline: 'NE SADA',
    declined: 'Odbijeno.',
    declinedBody: 'Ništa nije zapisano. Niko ne dobija poruku o tome.',
    waitingOnThem: (name: string) => `Čeka se ${name}.`,
    expiresIn: (when: string) => `Izazov ističe ${when}.`,

    /* the clock */
    started: 'DVOBOJ JE POČEO',
    briefIs: (title: string) => `Zadatak: ${title}`,
    yourTurn: 'Šta radiš?',
    submit: 'PREDAJ',
    submitTitle: 'Predaj rad',
    submitHint: 'Nalepi link na svoj post u zajednici. Ide u dvoboj i broji se kao okačen rad.',
    submitted: 'Tvoj rad je predat.',
    waitingForTheirs: 'Čeka se drugi rad.',
    bothIn: 'Oba rada su stigla.',

    /* the menu, in the FIGHT / BAG / POKéMON / RUN positions */
    menuSubmit: 'PREDAJ',
    menuBrief: 'ZADATAK',
    menuOpponent: 'PROTIVNIK',
    menuLeave: 'IZAĐI',

    /* the vote */
    voting: 'ZAJEDNICA ODLUČUJE',
    votingLeft: (when: string) => `Glasanje se zatvara ${when}.`,
    votePrompt: 'Šta je presudilo?',
    voteOpen: 'POGLEDAJ ↗',
    voteFor: (label: string) => `RAD ${label}`,
    voteChoose: 'IZABERI',
    voteChosen: '✓ IZABRANO',
    voteSubmit: 'GLASAJ',
    voteBlind: 'Ne piše čiji je koji rad. Namerno.',
    voteCast: 'Glas je zabeležen.',
    voteChange: 'Možeš da se predomisliš dok se glasanje ne zatvori.',
    voteWaiting: 'Dva rada čekaju tvoj glas.',
    /* Written whole per axis: „presudio" agrees with ritam and rez, „presudila" with boja i ideja. */
    decided: {
      ritam: 'Presudio je RITAM.',
      rez: 'Presudio je REZ.',
      zvuk: 'Presudio je ZVUK.',
      boja: 'Presudila je BOJA.',
      ideja: 'Presudila je IDEJA.',
    } as Record<string, string>,
    /* The move name in the battle log. Present tense, so it never guesses a gender. */
    uses: (name: string, axis: string) => `${name} koristi ${axis.toUpperCase()}!`,
    superEffective: 'Veoma efikasno!',

    /* the outcome */
    won: 'POBEDA.',
    /* Not „poraz". A loss is a thing that happened, not a thing somebody is. */
    lost: 'Ovaj put ne.',
    /* Never „ide Ani“: „ide“ takes the dative and a name cannot be declined safely. The
       subject is the work, which is masculine, so the sentence needs no case and no gender. */
    wonBy: (name: string) => `Bolji je rad — ${name}.`,
    walkover: 'Drugi rad nije stigao.',
    walkoverBody: 'Bez dva rada nema šta da se uporedi. Tvoj rad stoji, i broji se.',
    drawFew: 'Nije bilo dovoljno glasova.',
    drawFewBody: 'Manje od tri glasa nije zajednica koja odlučuje, pa se ne broji kao ishod.',
    drawTied: 'Nerešeno.',
    nothing: 'Dvoboj je istekao.',
    nothingBody: 'Niko nije predao rad. Ništa nije zapisano.',
    youWon: (axes: string) => `Tvoj rad je bio bolji po: ${axes}.`,
    youLostBut: (axes: string) => `Tvoj rad je ipak bio bolji po: ${axes}.`,

    /* the ending, which is the point of the whole thing */
    revise: 'POPRAVI I OKAČI',
    reviseTitle: 'Popravka',
    /* Said differently to each side, because „i kad si pobedio" to somebody who just lost is
       the app not listening. Both get the same button; only the sentence changes. */
    reviseWhy: 'Popravka vredi više od prvog pokušaja.',
    reviseWhyWon: 'Popravka vredi više od prvog pokušaja. I kad si pobedio.',
    reviseHint: 'Zajednica ti je rekla šta da gledaš. Vrati se u projekat i okači novu verziju.',
    revised: 'Popravka je okačena.',
    revisedBody: 'To je korak koji većina preskoči.',

    /* the post that brings the voters */
    postHint: 'Kopiraj ovo i okači na Skool. Bez linkova na radove — to bi odalo čiji je koji.',
    postCopy: 'KOPIRAJ',
    postCopied: 'Kopirano.',
    postDraft: (brief: string, url: string, closes: string) =>
      `DVOBOJ · ${brief}\n\n` +
      'Dva rada, isti zadatak, isti rok. Ne piše čiji je koji.\n' +
      'Pogledaj oba i reci šta je presudilo: ritam, rez, zvuk, boja ili ideja.\n\n' +
      `Glasa se u igri: ${url}\n` +
      `Glasanje se zatvara ${closes}.`,

    /* the other person is looking at the same screen right now. An event, said once. */
    watching: (name: string) => `${name} gleda isti ekran.`,
    back: 'NAZAD',

    /* getting out */
    forfeit: 'ODUSTANI OD DVOBOJA',
    forfeitConfirm: 'Dvoboj se prekida za oboje. Nema ishoda i ništa se ne zapisuje.',
    forfeited: 'Dvoboj je prekinut.',

    /* why a button is not there */
    refusals: {
      'sam-sebi': 'Sam sa sobom ne ide.',
      'van-posade': 'Jedno od vas dvoje nije u posadi.',
      /* Also returned when the OTHER person has not got here yet, so it cannot say „ti“. */
      'nije-otkljucano': 'Dvoboj se otvara na drugom pređenom ostrvu — za oboje.',
      'vec-traje': 'Već imate dvoboj u toku.',
      'previse-poziva': 'Previše izazova čeka odgovor. Sačekaj da neko odgovori.',
      'skoro-odbijen': 'Nedavno je odbijeno. Probaj kroz nedelju dana.',
      'nema-zadatka': 'Nemate zajednički zadatak za dvoboj.',
      istekao: 'Članstvo ti je isteklo.',
      'ti-si-u-njemu': 'Ovo je tvoj dvoboj. U njemu se ne glasa.',
      'nije-vreme': 'Nije vreme za to.',
      'nije-tvoj': 'To nije tvoj dvoboj.',
      'vec-si': 'To si već uradio.',
      'nije-gotov': 'Dvoboj još nije gotov.',
      'nisi-predao': 'Nisi predao rad u ovom dvoboju.',
    } as Record<string, string>,
  },

  submit: {
    badUrl: 'To nije link.',
    notSkool: 'Treba link na post u zajednici, ne na fajl.',
    duplicate: 'Taj link si već okačio.',
    sameAsFirst: 'Isti link kao prva verzija. Treba nova.',
    tooLong: 'Link je predugačak.',
    saved: 'Zapisano',
    savedOffline: 'Sačuvano. Ode čim se mreža vrati.',
    failed: 'Nije prošlo. Probaj opet.',
    rejectedOffline: (n: number) =>
      n === 1
        ? 'Jedan rad koji je čekao nije prošao. Okači ga ponovo.'
        : `${n} rada koja su čekala nisu prošla. Okači ih ponovo.`,
  },

  toast: {
    posted: 'Okačeno',
    postedBody: 'Sad čekaš feedback. Javi kad ga dobiješ.',
    revised: 'Popravljeno',
    revisedBody: 'To je korak koji većina preskoči.',
    done: 'Završeno',
    islandCleared: (island: string) => `Ostrvo ${island} pređeno`,
    islandOpened: (island: string) => `Otvorilo se ostrvo ${island}`,
    newScreen: (screen: string) => `Otključao si: ${screen}`,
  },

  /**
   * Souvenirs, keyed by the ids in mapa.json. The sentence is written out whole because Serbian
   * cases do not survive string concatenation — „Nosiš kapa" is exactly the register of a bad
   * translation. An id missing here still works; the toast just falls back to the island's goal.
   */
  souvenir: {
    kapa: { naziv: 'kapa', nosis: 'Nosiš kapu.' },
    slusalice: { naziv: 'slušalice', nosis: 'Nosiš slušalice.' },
  } as Record<string, { naziv: string; nosis: string }>,

  /**
   * The crew.
   *
   * One rule runs through every string here and it is not stylistic: **never conjugate a verb for
   * another member.** The rest of the product speaks to one person, so the masculine past tense it
   * has always used („Okačio si") is a register, not a claim about anybody. The moment a sentence
   * is about a *third* person whose gender the app does not know, that same form starts guessing —
   * and „Ana ti je ostavio komentar" is exactly the sentence that tells a member this thing was
   * not built for them.
   *
   * So: the subject is always the comment, never the commenter („Stigao ti je komentar" — masculine
   * because *komentar* is), and the person appears as a bare name next to it. It reads better than
   * the conjugated version anyway, and it never has to be revisited.
   */
  posada: {
    title: 'Posada',
    lead: 'Ljudi koji su na istim ostrvima kao ti.',
    whoTitle: 'Ko je gde',
    nobody: 'Trenutno nema nikoga na tvojim ostrvima.',
    worksTitle: 'Čeka komentar',
    worksLead: 'Radovi koje si ti već prešao. Otvori post, napiši šta bi menjao, pa se vrati.',
    nothingWaiting: 'Nema rada koji čeka, a koji si ti već prešao.',
    /* Said out loud rather than cut silently: a short list must not read as an empty queue. */
    moreWaiting: (n: number) => `Čeka još ${counted(n, WORDS.rad)}. Pojaviće se kako ove rešiš.`,
    someoneWaiting: (name: string) => `${name} čeka komentar`,
    doing: (title: string) => `radi na: ${title}`,
    idle: 'nema ništa otvoreno',
    onIsland: (island: string) => `ostrvo ${island}`,
    posted: (when: string) => `okačeno ${when}`,
    answered: 'već ima komentar',

    /* leaving one */
    openPost: 'OTVORI POST ↗',
    commented: 'OSTAVIO SAM KOMENTAR',
    commentHint: 'Komentar ide u zajednicu, na sam post. Ovde samo javiš da si ga ostavio.',
    commentSent: 'Javljeno',
    commentSentBody: 'Sad može da popravi rad.',

    /* receiving one */
    gotTitle: 'Stigao ti je komentar',
    gotOn: (title: string) => `na „${title}“`,
    gotLead: 'Komentar je na tvom postu u zajednici.',
    seeComment: 'VIDI KOMENTAR ↗',
    thanks: 'HVALA',
    thanked: 'zahvalio si se',
    giveBack: 'VRATI KOMENTAR',
    missing: 'nema ničega tamo',
    missingAsk: 'Nema komentara na tvom postu? Javi Marku, pogledaće.',
    missingDone: 'Javljeno Marku.',
    from: (name: string) => `— ${name}`,

    /* the payoff */
    echoRevised: 'Tvoj komentar je proizveo popravku.',
    echoRevisedBody: (name: string) => `${name} · to je korak koji većina preskoči.`,
    echoThanks: (name: string) => `Hvala ti — ${name}`,

    /* being in it, or not */
    meTitle: 'Ti u posadi',
    nameLabel: 'Kako te vide drugi',
    namePlaceholder: 'ime',
    nameHint: 'Ako ostaviš prazno, videće deo tvog mejla.',
    hide: 'Izađi iz posade',
    show: 'Uđi u posadu',
    hideHint:
      'Van posade te niko ne vidi na mapi, niko ti ne može ostaviti komentar, i ti ne možeš tuđim radovima.',
    hidden: 'Nisi u posadi.',
    saved: 'Sačuvano',

    /* why a button is not there */
    refusals: {
      'sam-sebi': 'To je tvoj rad.',
      'nisi-prosao': 'Ovaj čvor još nisi prešao.',
      'vec-si': 'Već si ostavio komentar na ovaj rad.',
      popravljeno: 'Rad je već popravljen.',
      'nije-okacen': 'Tu još nema rada.',
      'van-posade': 'Nisi u posadi.',
      istekao: 'Članstvo ti je isteklo.',
    } as Record<string, string>,
  },

  journal: {
    title: 'Dnevnik',
    lead: 'Upisuje se sam. Svaki okačen rad ti ostaje u portfoliju.',
    empty: 'Prazno je. Okači prvi rad.',
    week: (n: number) => `NEDELJA ${n}`,
    work: 'rad ↗',
    posted: (title: string) => `Okačeno: ${title}`,
    revisedEntry: (title: string) => `Popravljeno: ${title}`,
    finished: (title: string) => `Završeno: ${title}`,
    islandEntry: (island: string) => `Pređeno ostrvo ${island}`,
  },

  stats: {
    works: 'okačenih radova',
    revisions: 'popravljenih',
    islandLabel: 'ostrvo',
    dayLabel: 'dan',
    /* The only two numbers in the product. */
    line: (works: number, revisions: number) =>
      `${counted(works, WORDS.rad)} · ${revisions} ${plural(revisions, WORDS.popravljen)}`,
  },

  settings: {
    title: 'Podešavanja',
    pathLabel: 'Putanja',
    pathHint:
      'Sve što si već prešao ostaje. Storytelling, sound design i color važe u oba programa.',
    lookLabel: 'Izgled',
    lookEdit: 'PROMENI',
    save: 'SAČUVAJ',
    cancel: 'OTKAŽI',
  },

  /**
   * The wardrobe.
   *
   * Nothing in here is a reward and nothing in here is a number — that is the line this feature
   * has to stay on the right side of (GDD §2.1, §10.1). It says „izgled", never „otključano"; it
   * counts nothing; every option is available to everybody from the first minute. The souvenirs
   * are the only earned thing on the character and this screen never mentions them, because the
   * moment a wardrobe explains what you have *not* got, it has become a shop.
   */
  izgled: {
    title: 'Izgled',
    /* Three tabs. Nine rows on one screen is the failure mode this whole product was built against. */
    tabs: { lice: 'Lice', kosa: 'Kosa', odeca: 'Odeća' },
    axes: {
      skin: 'Koža',
      eyes: 'Oči',
      beard: 'Brada',
      extra: 'Dodatak',
      hair: 'Frizura',
      hairColor: 'Boja kose',
      outfit: 'Gornji deo',
      outfitColor: 'Boja',
      trousers: 'Pantalone',
    },
    /* Said once, quietly, under the preview. It is the whole policy of this screen. */
    hint: 'Menjaj kad hoćeš, koliko hoćeš. Ništa se ne zaključava i ništa se ne broji.',
    turn: 'Okreni',
    random: 'NASUMIČNO',
    save: 'SAČUVAJ',
    cancel: 'OTKAŽI',
    /* Onboarding: the presets are the question, this is the door past it for people who want it. */
    tweak: 'PRILAGODI',
    presets: 'Gotovi likovi',
    saved: 'Novi izgled je na tebi.',
  },

  admin: {
    title: 'Admin',
    members: 'Članovi',
    queue: 'Radovi',
    world: 'Svet',
    history: 'Istorija',
    refresh: 'OSVEŽI',
    metric:
      'Jedini broj koji vredi meriti: koliki procenat okačenih radova dobije popravljenu verziju. Cilj je 50%.',
    entered: (n: number) => `ušlo: ${n}`,
    addEmails: 'Dodaj mejlove',
    addEmailsHint: 'Jedan po redu, ili nalepi CSV export iz Skool-a.',
    addButton: 'DODAJ',
    membersAdded: 'Dodato',
    membersAddedBody: (added: number, updated: number) => `novih: ${added} · osveženih: ${updated}`,
    cols: {
      mejl: 'Mejl',
      putanja: 'Putanja',
      radovi: 'Radovi',
      popravljeni: 'Popravljeni',
      prviUlazak: 'Prvi ulazak',
      ko: 'Ko',
      kome: 'Kome',
      sta: 'Šta',
      kad: 'Kad',
      link: 'Link',
    },
    claims: 'Komentari članova',
    claimsHint:
      'Sam komentar je u zajednici, na postu — ovde piše ko je javio da ga je ostavio. Crveno znači da onaj kome je namenjen kaže da ga nema.',
    claimMissing: 'nema ga',
    claimThanked: 'zahvalio se',
    claimRevised: 'proizveo popravku',
    never: 'nikad',
    post: 'post ↗',
    dupLink: 'isti link je okačio još neko',
    issueLink: 'Link za ulaz',
    linkCopied: 'Link kopiran. Kod je uz njega, važe 15 minuta.',
    markFeedback: 'DAO SAM FEEDBACK',
    markedFeedback: 'Označeno',
    noSubmissions: 'Još nema okačenih radova.',
    revoke: 'Ukloni pristup',
    revokeAsk: (email: string) => `Ukloni pristup za ${email}? Napredak ostaje.`,
    restore: 'Vrati pristup',
    makeAdmin: 'Postavi za admina',
    makeAdminAsk: (email: string) => `Postavi ${email} za admina?`,
    removeAdmin: 'Skini admina',
    removeAdminAsk: (email: string) => `Skini admina sa ${email}?`,
    roleSaved: 'Sačuvano',
    roleSelf: 'Sebi ne možeš da menjaš ulogu.',
    roleRevoked: 'Prvo vrati pristup.',
    lastAdmin: 'Mora da ostane najmanje jedan admin sa pristupom.',
  },

  /**
   * The world builder — everything an admin sees while editing the curriculum. Same register as
   * the rest of the product: short, direct, no ceremony. "Nacrt" and "objavi" are the two words
   * the whole tool stands on: nothing a student sees changes until OBJAVI.
   */
  editor: {
    /* draft status */
    draftTitle: 'Nacrt sveta',
    draftClean: 'Nacrt je isti kao objavljena verzija.',
    draftDirty: 'Nacrt ima izmene koje još nisu objavljene.',
    activeVersion: (v: number) => `objavljena verzija ${v}`,
    lastEdit: (who: string) => `menjao ${who}`,
    saving: 'Čuvam…',
    saved: 'Sačuvano',
    saveFailed: 'Čuvanje nije prošlo. Proveri mrežu.',
    cantSave: 'Ne može da se sačuva dok ovo ne popuniš:',
    conflict: 'Nacrt je u međuvremenu izmenjen sa drugog mesta.',
    conflictLoad: 'UČITAJ NOVIJU VERZIJU',
    conflictHint:
      'Tvoje izmene od tog trenutka nisu sačuvane i izgubiće se kad učitaš noviju verziju. Prvo ih preuzmi ako ti trebaju, pa se dogovorite ko menja.',
    conflictSave: 'PREUZMI MOJU VERZIJU',

    /* actions */
    publish: 'OBJAVI',
    discard: 'ODBACI NACRT',
    /* Nacrt je zajednički: ovo briše i tuđe izmene, i to mora da stoji u samom pitanju. */
    discardConfirm:
      'Vrati nacrt na objavljenu verziju? Nestaju sve izmene u nacrtu — i tvoje i tuđe. Objavljeno se ne menja.',
    discardYes: 'ODBACI IZMENE',
    keep: 'ZADRŽI',
    publishBlocked: 'Ispravi greške ispod pa objavi.',
    publishBlockedSaving: 'Čekam da se sačuva.',

    /* structure */
    islands: 'Ostrva',
    addIsland: 'DODAJ OSTRVO',
    islandNamePlaceholder: 'ime novog ostrva',
    addNode: 'DODAJ ČVOR',
    nodeTitlePlaceholder: 'naslov novog čvora',
    firstNodeTitle: 'Prve lekcije',
    moveUp: 'Pomeri gore',
    moveDown: 'Pomeri dole',
    delete: 'OBRIŠI',
    deleteIslandAsk: (island: string, nodes: string) => `Obriši ostrvo ${island} i ${nodes} na njemu?`,
    deleteIslandYes: 'OBRIŠI OSTRVO',
    deleteNodeAsk: (title: string) => `Obriši čvor „${title}“?`,
    deleteNodeYes: 'OBRIŠI ČVOR',
    noName: '(bez imena)',
    deleteHasProgress:
      'Napredak članova na obrisanom čvoru ostaje zapisan, samo se više ne vidi na mapi.',

    /* island form */
    islandName: 'Ime ostrva',
    islandGoal: 'Cilj — šta član odnese sa ovog ostrva',
    islandDay: 'Dan kad se otvara',
    islandDayHint: '0 = odmah. Skool brave: 7, 31, 38, 46, 53, 56, 61, 68, 90.',
    islandWorks: 'Koliko okačenih radova traži',
    islandFree: 'Slobodno ostrvo',
    islandFreeHint:
      'Mesto, ne korak. Nema prethodnika, nema „4 od 6“, ne prelazi se i ne daje suvenir. Dodavanje vežbe ovde nikome ne pomera bravu.',
    nodeIsland: 'Ostrvo',
    nodeIslandHint: 'Premeštanje čuva id, pa se ne gubi ničiji rad ni popravka u toku.',
    after: 'Nudi se tek posle ostrva',
    afterHint: 'Ne zaključava — vežba se prosto ne nudi ranije. Isto kao horizont na mapi.',
    afterNone: 'odmah, bez čekanja',
    islandPaths: 'Na kojim putanjama postoji',
    souvenir: 'Suvenir za pređeno ostrvo',
    souvenirNone: 'bez suvenira',
    souvenirCustomPlaceholder: 'id suvenira',
    souvenirNoArt:
      'Za ovaj id još nema sličice, pa se ništa ne vidi na liku dok je neko ne nacrta. Reci Marku.',
    souvenirTaken: (island: string) => `Već ga daje ostrvo ${island}.`,
    souvenirAllTaken:
      'Svi nacrtani suveniri su zauzeti. Ostavi „bez suvenira" ili upiši novi id i reci Marku da treba sličica.',
    biome: 'Izgled ostrva',
    biomeAuto: 'automatski',
    mapPosition: 'Gde stoji na mapi — x ide udesno, y nadole (jedno polje = jedan korak)',
    nodesTitle: (n: number) => `Čvorovi (${n})`,

    /* node form */
    idLabel: 'id',
    idHint: 'Id je zauvek — napredak članova je vezan za njega. Menja se naslov, ne id.',
    nodeTitle: 'Naslov',
    nodeKind: 'Tip',
    kindNames: {
      lekcije: 'lekcije — gleda i radi u hodu',
      vezba: 'vežba — kači se u zajednicu',
      veliki: 'veliki zadatak — kači se + popravka',
      riznica: 'riznica — resurs, nije zadatak',
      opciono: 'opciono — bonus, ne broji se',
    },
    estMin: 'Traje (minuta)',
    estMinHint: 'Za lekcije: stvarna dužina videa. Za vežbe: stvarno vreme rada, ne dužina tutorijala.',
    submission: 'Predaja',
    submissionNames: {
      nista: 'ništa — član samo označi da je završio',
      link: 'link na post u zajednici',
      'link+popravka': 'link + popravljena verzija posle feedbacka',
    },
    requires: 'Otvara se posle',
    requiresNone: 'ničega — otvoren čim se otvori ostrvo',
    requiresMissing: (id: string) => `${id} — ne postoji pre ovog čvora`,
    task: 'Zadatak — šta tačno da uradi',
    taskHint: 'Piši članu, ne o članu. „Napravi… okači…“ Ovo je tekst na kartici SADA.',
    noteText: 'Napomena na čvoru',
    noteWho: 'Autor napomene',
    noteWhoHint: 'Ime kako će se videti članovima, npr. „Marko" ili „Danijela M."',
    universal: 'Univerzalan — važi i kad promeni putanju',
    preDone: 'Računa se kao završen odmah po registraciji',
    preDoneHint: 'Samo za ono što je član stvarno već uradio (profil). Ništa se ne poklanja.',
    skool: 'Skool lekcije',
    skoolCourse: 'Kurs (id iz Skool linka)',
    skoolCourseHint:
      'Iz linka skool.com/talasakademija/classroom/44b68573?md=… → kurs je 44b68573, a ono posle md= je lekcija.',
    skoolLessons: 'Lekcije — jedan id po redu',
    skoolUrl: 'Ili ceo link (preskače šablon)',
    skoolOpen: 'otvori ↗',
    perPathSame: 'spoji u jedno',
    perPathSplit: 'razdvoj po putanji',

    /* paths */
    pathsTitle: 'Putanje',
    pathName: 'Ime',
    pathDesc: 'Opis',
    pathActive: 'Aktivna — može da se izabere',
    pathNote: 'Napomena (vidi se kad nije aktivna)',
    addPath: 'DODAJ PUTANJU',
    pathIdPlaceholder: 'id (kratko, malim slovima)',
    pathIdBad: 'Id počinje slovom i ima samo mala slova, cifre i crticu — najviše 30 znakova.',
    pathInUse: (islands: string) => `Koristi je ${islands} — može samo da se isključi, ne i da se obriše.`,
    pathDelete: 'OBRIŠI PUTANJU',
    pathDeleteAsk: 'Obriši putanju? Nijedno ostrvo je ne koristi.',

    /* world settings */
    worldSettings: 'Podešavanja sveta',
    lessonTemplate: 'Šablon linka na lekciju',
    lessonTemplateHint: '{course} i {lesson} se zamene id-jevima.',
    communityUrl: 'Link na zajednicu',

    /* validation */
    checkTitle: 'Provera',
    checkClean: 'Sve je u redu.',
    checkCount: (errors: string, warnings: string) => `${errors}, ${warnings}`,
    errorsBlock: 'Greške moraju da se isprave pre objave.',

    /* publish */
    publishTitle: 'Objavi izmene',
    publishLead: 'Ovo vide svi članovi, odmah. Ostrva koja su članovima već otvorena ostaju otvorena.',
    publishCounts: (added: number, changed: number, removed: number) =>
      `dodato: ${added} · promenjeno: ${changed} · uklonjeno: ${removed}`,
    publishNote: 'Beleška za istoriju — šta se menja',
    publishNotePlaceholder: 'ukratko, za tebe i druge admine',
    publishConfirm: (v: number) => `OBJAVI KAO VERZIJU ${v}`,
    publishCancel: 'OTKAŽI',
    published: (v: number) => `Objavljeno · verzija ${v}`,
    publishNoChanges: 'Nema izmena — nacrt je isti kao objavljena verzija.',
    publishNoVisibleDiff: 'Izmene su u delovima koji se ne prikazuju u pregledu (komentari, verzija).',
    publishConflict: 'Nacrt se promenio u međuvremenu. Pogledaj pa probaj ponovo.',
    impactTitle: 'Ovo dira postojeće članove',
    /* Written whole per count via plural(): Serbian numerals 2-4 take a plural verb. */
    impactRemovedNode: (id: string, n: number) =>
      `Brišeš čvor ${id} — ${counted(n, WORDS.clan)} ${plural(n, VERBS.ima)} napredak na njemu. Napredak ostaje zapisan, ali nestaje sa mape.`,
    impactRemovedIsland: (id: string, n: number) =>
      `Brišeš ostrvo ${id} — ${counted(n, WORDS.clan)} ${plural(n, VERBS.jeStajao)} na njemu.`,
    impactSubmission: (id: string, n: number) =>
      `Menjaš predaju na čvoru ${id} — ${counted(n, WORDS.clan)} ${plural(n, VERBS.jeOkacio)} rad na njemu.`,

    /* preview */
    previewTitle: 'Proba',
    previewLead: 'Kako svet izgleda članu, iz nacrta. Ništa se ne čuva.',
    previewBroken: 'Proba ne radi dok u nacrtu ima grešaka. Pogledaj Proveru.',
    previewPath: 'Putanja',
    previewDay: 'Dan članstva',
    previewCleared: 'Koliko je prvih ostrva prešao',
    previewBudget: 'Koliko vremena ima',
    previewAnyTime: 'bilo šta',
    previewPending: 'Poslednji veliki zadatak čeka popravku (stigao mu je feedback)',
    previewStats: (works: string) => `takav član ima ${works}`,
    previewDayRaised: (day: number) =>
      `dan je podignut na ${day}, jer se ta ostrva ne mogu preći ranije`,
    previewCard: 'Kartica SADA bi rekla:',
    previewLocked: 'zaključano',
    previewOpen: 'otvoreno',
    previewCard_popravka: (title: string) => `Popravka: ${title}`,
    previewCard_rad: (title: string, min: string) => `${title} · ${min}`,
    previewCard_brava: (island: string, blocker: string) => `Brava: ${island} — ${blocker}`,
    previewCard_kraj: 'Prešao je sve što je otvoreno.',
    previewCard_cekaFeedback: 'Čeka feedback na okačen rad.',
    previewCard_nista: (min: number) => `Ništa ne staje u ${min} min.`,

    /* history */
    historyTitle: 'Objavljene verzije',
    historyEmpty: 'Iz editora još ništa nije objavljeno. Važi sadržaj koji je stigao sa kodom.',
    historyActive: 'važeća',
    historyRestore: 'VRATI U NACRT',
    historyRestoreAsk:
      'Ova verzija zamenjuje trenutni nacrt. Objavljeno se ne menja dok ne pritisneš OBJAVI.',
    historyRestoreYes: 'ZAMENI NACRT',
    historyRestored: 'Vraćeno u nacrt',
    historyDownload: 'PREUZMI JSON',
    downloadDraft: 'PREUZMI NACRT (mapa.json)',
    importTitle: 'Uvezi JSON',
    importHint: 'Nalepi ceo mapa.json. Postaje nacrt — ništa se ne objavljuje samo od sebe.',
    importButton: 'UVEZI U NACRT',
    importBadJson: 'Ovo nije ispravan JSON — nešto je iseckano ili nedostaje zagrada.',
    importBadSchema: 'JSON je ispravan, ali nije mapa sveta — nema ostrva u očekivanom obliku.',
    importOk: 'Uvezeno u nacrt',
    gitHint:
      'Svaka objavljena verzija je ovde. Preuzmi je i pošalji Marku ako želiš kopiju van aplikacije — čuvaju se poslednjih 60.',
  },

  /**
   * TEREN — the world builder.
   *
   * The vocabulary is deliberately not a level editor's. Nobody here is a level designer: it is
   * „četkica" and „kanta", not „brush" and „fill", and a tile is „polje" the way a square on a
   * board is. The one piece of jargon kept is „dok", because that is what the thing is called on
   * the map the admin is drawing.
   */
  teren: {
    title: 'Teren',
    back: '← NAZAD NA OSTRVO',
    tabData: 'Podaci',
    tabTerrain: 'Teren',

    openDrawn: 'UREDI TEREN',
    openAuto: 'NACRTAJ TEREN',
    broken: 'Nacrtani teren ovog ostrva ne može da se pročita.',
    brokenHint:
      'Ovo se ne dešava iz editora — neko je menjao JSON ručno ili je fajl isečen. Objavljena verzija je netaknuta: možeš da je vratiš iz Istorije, ili da kreneš ponovo od automatskog oblika.',
    emptyTitle: 'Ovo ostrvo se još crta samo.',
    emptyLead:
      'Oblik, staza i mesta čvorova se računaju iz spiska čvorova. Ako hoćeš da ga crtaš rukom, kreni od automatskog oblika — pa menjaj šta hoćeš.',
    seed: 'KRENI OD AUTOMATSKOG OBLIKA',
    reseed: 'VRATI NA AUTOMATSKI OBLIK',
    reseedAsk: 'Ovo baca sve što si nacrtao na ovom ostrvu — teren, likove i ukrase. Ostrvo se vraća na automatski oblik.',
    reseedYes: 'BACI NACRTANO',

    /* tools */
    tools: 'Alat',
    toolBrush: 'četkica',
    toolRect: 'pravougaonik',
    toolFill: 'kanta',
    toolPick: 'kapaljka',
    toolObjects: 'objekti',
    toolErase: 'gumica',
    brushSize: 'Debljina',
    undo: 'Poništi',
    /* „Vrati" bi se čitalo kao poništavanje, a i stoji na dugmetu za vraćanje na automatski oblik. */
    redo: 'Ponovi',
    undoBack: 'PONIŠTI',
    redoBack: 'VRATI NACRT',
    redoBackHint:
      'Nacrt nije izgubljen — poništio si jedan korak previše. „Vrati nacrt" ga donosi nazad. Ako umesto toga kreneš od automatskog oblika, nacrt se briše.',
    saveConflict: 'Nacrt je izmenjen sa drugog mesta — ovo se više ne čuva. Klikni.',
    zoomIn: 'Uvećaj',
    zoomOut: 'Umanji',
    grid: 'Mreža',

    /* palettes */
    paletteTerrain: 'Šta crtaš',
    paletteObjects: 'Šta postavljaš',
    objectDock: 'Dok — tu član stiže na ostrvo',
    objectNodes: 'Čvorovi',
    objectCharacters: 'Likovi',
    objectProps: 'Ukrasi',
    nodePlaced: 'postavljen',
    nodeMissing: 'nije postavljen',
    placeHint: 'Izabrao si šta postavljaš. Klikni na teren gde ide.',
    dragHint: 'Uzmi i prevuci da pomeriš. Delete sklanja izabrano. Klikni na prazno da poništiš izbor.',
    paintHint: 'Klikni i prevuci po terenu. Kapaljka uzima polje na koje klikneš.',
    /* Gumica sklanja stvari, ne teren: teren nema „prazno", a četkica i kapaljka su već njegova guma. */
    eraseHint: 'Klikni na lik, ukras ili čvor da ga skloniš. Teren se ne briše — prebojiš ga četkicom.',
    /* Odbijanje na kursoru: jedna činjenica o svetu, nikad presuda o čoveku. Uvek samo jedan razlog. */
    noWater: 'Tu ne može — voda.',
    noSolid: 'Tu ne može — nešto stoji.',
    noTaken: 'Tu već nešto stoji.',
    cantRemoveDock: 'Dok ne može da se skloni — negde mora da se stiže.',

    /* size */
    size: 'Veličina terena',
    sizeHint: 'Polja, ne pikseli. Menjanje veličine seče ili dodaje more po ivicama.',
    sizeWidth: 'širina',
    sizeHeight: 'visina',

    /* inspector */
    inspector: 'Izabrano',
    nothingSelected: 'Ništa nije izabrano. Klikni na čvor, lik ili ukras na terenu.',
    characterName: 'Ime — kako se predstavlja',
    characterArt: 'Crtež',
    characterFacing: 'Gleda na',
    characterLines: 'Šta govori',
    characterLinesHint: 'Jedna poruka = jedan okvir na ekranu. Član ih pregleda redom.',
    addLine: 'DODAJ PORUKU',
    removeLine: 'Obriši poruku',
    /* Sivi tekst u praznom polju — nikad se ne čuva i nikad ne stiže do člana. */
    linePlaceholder: 'npr. Kad zapneš, okači nedovršeno. Bolje nego da stoji na disku.',
    /*
     * Ovo je prava rečenica, jer se STVARNO upisuje kad se lik postavi.
     * Placeholder počinje sa „npr." i kao poruka bi objavio uputstvo za admina članovima.
     */
    firstLine: 'Kad zapneš, okači nedovršeno. Bolje nego da stoji na disku.',
    removeObject: 'SKLONI SA TERENA',
    removeObjectAsk: 'Skloni sa terena?',
    removeObjectYes: 'SKLONI',
    nodeOnTerrain: (title: string) => `Čvor „${title}“`,
    nodeGoTo: 'OTVORI ČVOR',
    dockSelected: 'Dok — tu član stiže. Sve na ostrvu mora da se dohvati odavde.',

    /* status */
    statusSize: (w: number, h: number) => `${w}×${h} polja`,
    statusPlaced: (placed: number, total: number) => `${placed}/${total} čvorova postavljeno`,
    statusPeople: (n: number) => counted(n, WORDS.lik),
    statusProps: (n: number) => counted(n, WORDS.ukras),
    /* Srpski: „1 čvor još nema", „2 čvora još nemaju", „5 čvorova još nema" — otud plural(). */
    unplacedWarning: (n: number) =>
      `${counted(n, WORDS.cvor)} još ${plural(n, VERBS.nema)} mesto na terenu — postavi ${
        n === 1 ? 'ga' : 'ih'
      } pre objave.`,
    unreachable: 'Do nečega na terenu ne može da se dođe od doka. Pogledaj Proveru.',
    errorsHere: (errors: string) => `${errors} na ovom ostrvu — dok se ne isprave, ne može da se objavi.`,

    /* characters seeded with one click */
    addPair: 'DODAJ MARKA I MARIJA',
    /* Dugme radi na svakom ostrvu, pa nijedna rečenica ne sme da tvrdi koje je ostrvo u pitanju. */
    markoLine1: 'Zdravo. Nema ovde ničeg što možeš da pokvariš.',
    markoLine2: 'Uzmi jednu vežbu, uradi je do kraja i okači je. Nedovršeno je i dalje bolje od ničega.',
    marioLine1: 'Feedback nije ocena. To je neko ko ti kaže gde da gledaš.',
    /* Ne „poen": u ovom proizvodu se ništa ne boduje, a i reč je „poenta". */
    marioLine2: 'Kad ga dobiješ, vrati se u projekat i okači popravljenu verziju. Tek taj korak se stvarno računa.',
  },

  /**
   * MOJE OSTRVO.
   *
   * Ovde nema nijednog broja i nijedne rečenice koja nekome kaže šta još nije uradio. Ostrvo se ne
   * ocenjuje, ne popunjava se do kraja i ne poredi se ni sa čijim. Ako se ikad pojavi „x od y",
   * pogrešili smo.
   */
  dom: {
    title: 'MOJE OSTRVO',
    /* Prvi put. Jedna rečenica, bez uputstva — ostrvo je već tu i već lepo izgleda. */
    intro: 'Ovo je tvoje. Niko ti ovde ništa ne zadaje.',
    walk: 'PROŠETAJ',
    edit: 'UREDI',
    done: 'GOTOVO',
    saving: 'Čuvam…',
    saved: 'Sačuvano',
    saveFailed: 'Nije sačuvano. Probaj ponovo.',
    /* Lepljivo stanje: neko je isto ostrvo menjao sa drugog uređaja. Ne prepisujemo tuđi rad. */
    stale: 'Ostrvo je izmenjeno na drugom uređaju. Osveži da nastaviš odavde.',
    reload: 'OSVEŽI',
    revert: 'VRATI KAKO JE BILO',
    revertHint: 'Vraća ostrvo na ono kako je izgledalo kad si počeo da ga sređuješ.',

    /* ime */
    name: 'Ime ostrva',
    nameHint: 'Dve reči. Menjaš kad hoćeš.',

    /* vrata */
    door: 'Ko može da dođe',
    doorClosed: 'Niko',
    doorCode: 'Ko ima šifru',
    doorOpen: 'Svi iz Talasa',
    doorCodeLabel: 'Šifra',
    doorCodeHint: 'Pošalji je kome hoćeš. Kad zatvoriš vrata, šifra prestaje da važi.',
    doorNeedsCrew: 'Dok si van posade, ostrvo je samo tvoje.',
    newCode: 'NOVA ŠIFRA',

    /* uređivanje */
    paintWhat: 'Šta crtaš',
    paintHint: 'Klikni i prevuci. Sa dva prsta pomeraš i zumiraš.',
    dragHint: 'Uzmi i prevuci da pomeriš. Izaberi nešto sa strane pa klikni gde ide.',
    eraseHint: 'Klikni na stvar da je skloniš. Teren se ne briše — prebojiš ga četkicom.',
    /* Činjenica o mestu, ne dozvola koja fali. */
    full: 'Tu nema mesta. Skloni nešto pa dodaj.',

    /* gosti */
    visit: 'IDI U GOSTE',
    visitCode: 'Unesi šifru',
    visitGo: 'UĐI',
    visitHint: 'Šifru ti pošalje onaj kod koga ideš.',
    visitBadCode: 'Ta šifra ne otvara ništa.',
    visitClosed: 'Vrata su zatvorena.',
    open: 'Otvorena ostrva',
    openEmpty: 'Za sada nema otvorenih vrata.',
    /*
     * „Kod" traži genitiv („kod Marka"), a ime se ne može pouzdano deklinirati — zato konstrukcija
     * koja ime ostavlja u nominativu. Isto rešenje kao svuda gde se u proizvodu pojavi tuđe ime.
     */
    guestOf: (name: string) => `U gostima — ${name}`,
    /* Gost nema alat. Nema ni rečenice da ga nema — nema šta da se uskraćuje. */
    home: 'NAZAD NA SVOJE',

    /* razglednica */
    postcard: 'RAZGLEDNICA',
    postcardHint: 'Slika tvog ostrva, spremna da je okačiš.',
  },

  error: {
    offline: 'Nema mreže',
    offlineBody: 'Radi dalje. Sve se čuva i ode čim se mreža vrati.',
    generic: 'Nešto je puklo',
    genericBody: 'Osveži stranicu. Ako se ponovi, javi Marku.',
    stale: 'Stigla je nova verzija',
    staleBody: 'Osveži da nastaviš.',
    reload: 'OSVEŽI',
  },

  a11y: {
    map: 'Mapa arhipelaga',
    listView: 'Prikaži spisak',
    mapView: 'Prikaži mapu',
    sound: 'Zvuk',
    close: 'Zatvori',
    duel: 'Ekran dvoboja',
  },
} as const;

export type Copy = typeof copy;

/**
 * What each kind of ground is called in the builder's palette.
 *
 * Kept out of the `copy` object because it is keyed by the Terrain enum rather than by a name a
 * person picked, and typed as a full Record so adding terrain without naming it is a compile
 * error — an unnamed swatch in a palette is a swatch nobody picks.
 *
 * „Daske" rather than „dok" for Terrain.Dock on purpose: the planks are one thing to paint, and
 * the dock is the single tile a member arrives on. Two names, because they are two decisions.
 */
/**
 * The five axes a duel can be decided on.
 *
 * Kept out of the `copy` object and typed as a full Record for the same reason as the terrain
 * palette: adding a sixth axis without naming it should be a compile error. It should also be
 * hard — five is a design decision (`docs/DVOBOJ.md` §3), not a starting point.
 *
 * One word each, and they are drawn as the move name on the battle screen as well as on the vote
 * button — which is why they are the bare noun rather than a phrase.
 */
export const OSA_LABELS: Record<Osa, string> = {
  ritam: 'RITAM',
  rez: 'REZ',
  zvuk: 'ZVUK',
  boja: 'BOJA',
  ideja: 'IDEJA',
};

export const TERRAIN_LABELS: Record<Terrain, string> = {
  [Terrain.Deep]: 'duboko more',
  [Terrain.Sea]: 'more',
  [Terrain.Shallow]: 'plićak',
  [Terrain.Sand]: 'pesak',
  [Terrain.Ground]: 'trava',
  [Terrain.Path]: 'staza',
  [Terrain.Cliff]: 'litica',
  [Terrain.Tree]: 'drvo',
  [Terrain.Rock]: 'stena',
  [Terrain.Fog]: 'magla',
  [Terrain.Dock]: 'daske',
  [Terrain.Bridge]: 'most',
  [Terrain.Flowers]: 'cveće',
  [Terrain.Bush]: 'žbun',
  [Terrain.Fence]: 'ograda',
  [Terrain.Hut]: 'koliba',
  [Terrain.TallGrass]: 'visoka trava',
};
